import { Head } from 'vite-react-ssg'

const SITE_NAME = 'Nala'
const SITE_URL = 'https://nala.vet'
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`

interface SEOHeadProps {
  title: string
  description: string
  canonical?: string
  ogImage?: string
  ogType?: 'website' | 'article'
  jsonLd?: object | object[]
  noIndex?: boolean
}

export default function SEOHead({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  jsonLd,
  noIndex = false,
}: SEOHeadProps) {
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} — ${SITE_NAME}`
  const canonicalUrl = canonical ?? SITE_URL
  const jsonLdArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : []

  return (
    <Head>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      {noIndex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={ogType} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD structured data */}
      {jsonLdArray.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Head>
  )
}

// ─── Pre-built schema helpers ─────────────────────────────────────────────────

export const NALA_BUSINESS_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': ['VeterinaryService', 'LocalBusiness'],
  name: 'Nala',
  description:
    'Licensed veterinarians dispatched directly to your home. Compassionate, professional vet care without the stress of a clinic visit.',
  url: SITE_URL,
  logo: `${SITE_URL}/logo.svg`,
  image: DEFAULT_OG_IMAGE,
  telephone: '+1-213-833-8007',
  email: 'support@nala.vet',
  serviceType: 'Mobile Veterinary Care',
  areaServed: {
    '@type': 'Country',
    name: 'United States',
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Nala Veterinary Services',
  },
  sameAs: [
    'https://www.facebook.com/nalavet',
    'https://www.instagram.com/nalavet',
    'https://twitter.com/nalavet',
  ],
}

export const WEBSITE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Nala',
  url: SITE_URL,
  description: 'Licensed veterinarians delivered to your door.',
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/book?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
}

export function buildServiceSchema(service: {
  name: string
  description: string
  price_cents: number
  duration_minutes: number
  slug: string
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.name,
    description: service.description,
    url: `${SITE_URL}/services/${service.slug}`,
    provider: {
      '@type': 'VeterinaryService',
      name: 'Nala',
      url: SITE_URL,
    },
    offers: {
      '@type': 'Offer',
      price: (service.price_cents / 100).toFixed(2),
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
    },
    serviceOutput: {
      '@type': 'MedicalProcedure',
      name: service.name,
    },
  }
}

export const BREADCRUMB_HOME = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: SITE_URL,
    },
  ],
}

export function buildBreadcrumbs(
  crumbs: Array<{ name: string; url: string }>,
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      ...crumbs.map((c, i) => ({
        '@type': 'ListItem',
        position: i + 2,
        name: c.name,
        item: c.url,
      })),
    ],
  }
}
